import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar,
} from "recharts";
import { ArrowUpRight, Buildings, CreditCard, Users, CurrencyDollar } from "@phosphor-icons/react";

import Layout, { PageHeader, Surface, StatusPill } from "@/components/Layout";
import { api } from "@/lib/api";
import { ADMIN } from "@/lib/routes";

const fmt$ = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtN = (v) => Number(v || 0).toLocaleString();

export default function Overview() {
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState({ series: [], top_merchants: [] });
  const [recentTx, setRecentTx] = useState([]);
  const [merchants, setMerchants] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/revenue?days=30"),
      api.get("/admin/transactions?limit=8"),
      api.get("/admin/merchants"),
    ]).then(([s, r, t, m]) => {
      setStats(s.data); setRevenue(r.data); setRecentTx(t.data.items); setMerchants(m.data.items);
    }).catch(() => {});
  }, []);

  const merchantsById = Object.fromEntries(merchants.map(m => [m.id, m.company_name]));

  const kpis = [
    { label: "Active Merchants", value: stats ? `${stats.merchants.active}` : "—", sub: stats ? `${stats.merchants.total} total` : "", icon: Buildings },
    { label: "Cards Issued", value: stats ? fmtN(stats.cards.total) : "—", sub: stats ? `${stats.cards.active} active` : "", icon: CreditCard },
    { label: "Cardholders", value: stats ? fmtN(stats.users.total) : "—", sub: "all merchants", icon: Users },
    { label: "Revenue (all time)", value: stats ? fmt$(stats.revenue) : "—", sub: stats ? `${fmt$(stats.transactions.volume)} processed` : "", icon: CurrencyDollar },
  ];

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="overview-page">
        <PageHeader
          title="Control Room"
          subtitle="Real-time view of the Blonix issuing fabric."
        />

        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="kpi-grid">
          {kpis.map((k) => (
            <Surface key={k.label} className="p-5" testId={`kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">{k.label}</div>
                  <div className="font-display text-3xl tracking-tight font-medium mt-2 tabular-nums">{k.value}</div>
                  <div className="text-xs text-zinc-500 mt-1">{k.sub}</div>
                </div>
                <div className="h-8 w-8 rounded-sm bg-zinc-100 flex items-center justify-center">
                  <k.icon size={16} weight="regular" className="text-zinc-700" />
                </div>
              </div>
            </Surface>
          ))}
        </div>

        {/* Chart + Recent */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <Surface className="lg:col-span-2 p-5" testId="revenue-chart-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">Last 30 days</div>
                <div className="font-display text-xl tracking-tight font-medium mt-0.5">Revenue</div>
              </div>
              <Link to={ADMIN.analytics} className="text-xs inline-flex items-center gap-1 text-zinc-700 hover:text-zinc-950">
                View analytics <ArrowUpRight size={12} />
              </Link>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenue.series} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                  <defs>
                    <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#09090B" stopOpacity={0.18} />
                      <stop offset="100%" stopColor="#09090B" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#F4F4F5" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#71717A" }} stroke="#E4E4E7" />
                  <YAxis tick={{ fontSize: 10, fill: "#71717A" }} stroke="#E4E4E7" />
                  <Tooltip contentStyle={{ borderRadius: 2, fontSize: 12, border: "1px solid #E4E4E7" }} />
                  <Area dataKey="revenue" type="monotone" stroke="#09090B" strokeWidth={1.5} fill="url(#rev)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Surface>

          <Surface className="p-5 flex flex-col" testId="recent-tx-card">
            <div className="flex items-center justify-between mb-4 shrink-0">
              <div className="font-display text-xl tracking-tight font-medium">Recent transactions</div>
            </div>
            <ul className="divide-y divide-zinc-100 h-64 overflow-y-auto sb-scroll">
              {recentTx.length === 0 && <li className="text-xs text-zinc-500 py-2">No transactions yet.</li>}
              {recentTx.map((t) => (
                <li key={t.id} className="py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{t.merchant_name || t.type}</div>
                    <div className="text-[10px] uppercase tracking-[0.05em] text-zinc-500">
                      {merchantsById[t.merchant_id] || "merchant"}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-mono tabular-nums">{fmt$(t.amount)}</div>
                    <StatusPill value={t.status} />
                  </div>
                </li>
              ))}
            </ul>
          </Surface>
        </div>

        {/* Top merchants */}
        <Surface className="p-5" testId="top-merchants-card">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display text-xl tracking-tight font-medium">Top merchants by revenue</div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={(revenue.top_merchants || []).map(m => ({
                name: merchantsById[m.merchant_id]?.slice(0, 16) || m.merchant_id.slice(0, 6),
                revenue: m.revenue,
              }))} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid stroke="#F4F4F5" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#71717A" }} stroke="#E4E4E7" />
                <YAxis tick={{ fontSize: 10, fill: "#71717A" }} stroke="#E4E4E7" />
                <Tooltip contentStyle={{ borderRadius: 2, fontSize: 12, border: "1px solid #E4E4E7" }} />
                <Bar dataKey="revenue" fill="#09090B" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Surface>
      </div>
    </Layout>
  );
}
